import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios';
import _ from 'lodash';
import createPersistedState from "vuex-persistedstate";

import { GoodsObject } from './models/GoodsObject';
import { NotificationObject } from './models/NotificationObject';
import { RolesService } from './services/RolesService';

Vue.use(Vuex)

const store = new Vuex.Store({
  plugins: [ createPersistedState({ key: 'staticData', paths: ['staticData'] }) ],
  state: {
    language: 'en',
    user: {
      loggedIn: false,
      role: [''],
      _id: -1,
      isAdmin: false,
      subscribedChannels: [],
      operatorSession: -1,
      operatorId: -1
    },
    snackbar: {
      serverMessage: '',
      show: false,
      color: ''
    },
    goods: {
      items: Array<GoodsObject>(),
      idToIndex: Object()
    },
    validation: Object(),
    featureFlags: {
      initialized: false
    },
    app: {
      rolesArray: [],
    },
    notifications: {
      allNotifications: Array<NotificationObject>(),
      idToIndex: Object(),
      unreadCount: 0
    },
    staticData: {
      'packing-level': {
        plA: Array<string>(),
        plB: Array<string>(),
        plC: Array<string>()
      },
      'company-detail': Object()
    },
  },
  mutations: {
    setUser (state, user) {
      state.user = user;
      state.user.loggedIn = !!user;
    },
    resetUser (state) {
      state.user = {
        loggedIn: false,
        role: [''],
        _id: -1,
        isAdmin: false,
        subscribedChannels: [],
        operatorSession: -1,
        operatorId: -1
      };
    },
    setSnackbar(state, snackbar: { serverMessage: string, show: boolean, color: string } = { serverMessage: '', show: false, color: '' }) {
      state.snackbar = snackbar;
    },
    addMultipleGoods(state, inputGoods: GoodsObject[]) {
      const items = _.unionBy(inputGoods.map(good => new GoodsObject(good, good.ftProcessRow)), state.goods.items, '_id' );
      const idToIndex: any = items.reduce((obj, currentGood, currentIndex) => Object.assign(obj, {[currentGood._id]: currentIndex}), {});
      state.goods = Object.assign({}, state.goods, { items, idToIndex});
    },
    updateSingleGood(state, inputGood: any) {
      if(state.goods.idToIndex[inputGood._id]) {
        Vue.set(state.goods.items, state.goods.idToIndex[inputGood._id], inputGood);
      } else {
        Vue.set(state.goods.items, state.goods.items.length, inputGood);
        Vue.set(state.goods.idToIndex, inputGood._id, state.goods.items.length - 1);
      }
    },
    resetGoods(state) {
      state.goods = Object.assign({}, state.goods, { items: [], idToIndex: {} });
    },
    removeGoods(state, id: any) {
      Vue.delete(state.goods.items, state.goods.idToIndex[id])
      Vue.delete(state.goods.idToIndex, id)
    },
    setFormValidStatus(state, validationObject: any) {
      state.validation = { ...state.validation, ...validationObject };
    },
    setFeatureFlagInitializedStatus(state, initialized: boolean) {
      state.featureFlags.initialized = initialized;
    },
    setAppData(state, data) {
      state.app = Object.assign({}, state.app, data);
    },
    setNotifications(state, { allNotifications }: { allNotifications: NotificationObject[] }) {
      const idToIndex: any = allNotifications.reduce((obj, currentGood, currentIndex) => Object.assign(obj, { [currentGood._id]: currentIndex }), {});
      state.notifications = Object.assign({}, state.notifications, { allNotifications, idToIndex });
    },
    updateSingleNotification(state, notification: NotificationObject) {
      if(state.notifications.idToIndex[notification._id]) {
        Vue.set(state.notifications.allNotifications, state.notifications.idToIndex[notification._id], notification);
      } else {
        const allNotifications = [ notification, ...state.notifications.allNotifications ];
        Vue.set(state.notifications, 'allNotifications', allNotifications);
        Vue.set(state.notifications.idToIndex, notification._id, 0);
      }
    },
    setStaticData(state, staticData: any) {
      state.staticData = Object.assign({}, state.staticData, staticData);
    },
    setUnreadNotificationCount(state, unreadCount: number) {
      state.notifications.unreadCount = unreadCount;
    },
    setLanguage (state, language) {
      state.language = language;
    },
  },
  actions: {
    async refreshUser(context) {
      try {
        const user = (await axios.get('/api/v1/users/loginuser')).data;
        context.commit('setUser', user)
      } catch (error) {
        context.commit('setSnackbar', { serverMessage: error.err, show: true, color: 'error' });
      }
    }
  },
  getters: {
    getErrorFor: (state: any) => (errorName: string, container : string = 'default') => {
      const errorObject = _.get(state, `validation,${container}.${errorName}`, '');
      return _.get(errorObject,'message', errorObject);
    },
    getGoodFromId: (state: any) => (goodId: string) => {
      const goodIndex: number = state.goods.idToIndex[goodId];
      return state.goods.items[goodIndex]; 
    },
    roleMatches: (state: any) => (roles: any, singleRole?: string) => {
      if (roles == null || RolesService.allRoles == null) return false;
      const rolesKeyArray = Array.isArray(roles) ? roles : roles.split(',');
      const rolesArray = rolesKeyArray.map((roleKey: string) => RolesService.allRoles[roleKey]);
      if (singleRole) {
        if (rolesArray.includes(singleRole)) {
          if(singleRole == 'Production | Operator') {
            return state.user.operatorId > -1;
          }
          return true;
        }
        return false;
      }
      for (const role of rolesArray) {
        if (state.user.role.includes(role)) {
          if(role == 'Production | Operator') {
            return state.user.operatorId > -1;
          }
          return true;
        }
      }
      return false;
    },
    role: (state: any) => {
      return state.user.role.join(',');
    },
    appData: (state: any) => (key: string) => {
      return state.app[key];
    },
    getPackingLevelFromSellingUnit: (state: any) => (sellingUnit: string, packingMode: any[] ) => {
      const packingLevels = _.get(state, 'staticData.packing-level');
      if(!packingLevels) return ''
      if(packingLevels.plA.includes(sellingUnit) && sellingUnit == _.get(packingMode, '0.unit1', '')) {
        return 'plA';
      }
      if(packingLevels.plB.includes(sellingUnit) && sellingUnit == _.get(packingMode, '0.unit2', '')) {
        return 'plB';
      }
      if(packingLevels.plC.includes(sellingUnit)) {
        return 'plC';
      }
    return '';
    },
  },
})

export default store